import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import {
  Button,
  IconClose,
  IconStar2,
  Input,
  StatusBadge,
  cn,
  useOverlayMotion,
} from '@taki/ui';

export interface AskTakiAIProps {
  /** Ouvert (rendu) ou non. */
  open: boolean;
  /** Ferme le panneau. */
  onClose: () => void;
}

/** Quelques pistes d'amorce (coquille v0 : pas de backend). */
const SUGGESTIONS: readonly string[] = [
  'Résume mes derniers contacts',
  'Quelles apps sont installées ?',
  'Crée une activité pour demain',
];

/**
 * Panneau "Ask Taki AI" : assistant conversationnel du shell.
 *
 * Coquille v0 (placeholder, aucun backend branché) : panneau lateral droit
 * en surface + bordure, en-tete sparkle dans une pastille lime accent, zone
 * de conversation avec etat d'accueil, et une barre de saisie en bas
 * (Input leftIcon sparkle + Button ai). L'envoi ne fait rien d'autre que
 * vider le champ. Rien n'est rendu si `open` est faux.
 */
export function AskTakiAI({ open, onClose }: AskTakiAIProps): JSX.Element | null {
  const [draft, setDraft] = useState('');

  // Cycle de vie anime (norme du socle) : reste monte le temps de
  // l'animation de sortie, cf. useOverlayMotion.
  const { mounted, closing } = useOverlayMotion(open);

  if (!mounted) return null;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // Coquille v0 : pas de backend. On vide simplement le champ.
    setDraft('');
  }

  return (
    <div className={cn('fixed inset-0 z-40 flex justify-end', closing && 'pointer-events-none')}>
      {/* Scrim : ferme au clic en dehors du panneau. */}
      <button
        type="button"
        aria-label="Fermer Ask Taki AI"
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-bg/60 backdrop-blur-sm',
          closing ? 'taki-anim-fade-out' : 'taki-anim-fade',
        )}
      />

      {/* Panneau lateral droit. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Ask Taki AI"
        className={cn(
          'relative z-10 flex h-full w-[26.25rem] max-w-full flex-col',
          'border-l border-border bg-surface shadow-panel',
          'sm:my-3 sm:mr-3 sm:h-[calc(100%-1.5rem)] sm:rounded-2xl sm:border',
          closing ? 'taki-anim-pop-out' : 'taki-anim-pop',
        )}
      >
        {/* En-tete. */}
        <header className="flex items-center justify-between gap-3 border-b border-border-soft px-5 py-4">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent-tint text-accent-soft-ink"
            >
              <IconStar2 size={18} />
            </span>
            <div className="flex flex-col">
              <span className="text-[0.9375rem] font-semibold leading-tight text-ink-900">
                Ask Taki AI
              </span>
              <span className="text-xs leading-tight text-ink-400">
                Assistant
              </span>
            </div>
          </div>

          <button
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-full',
              'text-ink-400 transition-colors hover:bg-surface-3 hover:text-ink-900',
            )}
          >
            <IconClose size={18} />
          </button>
        </header>

        {/* Corps : zone de conversation (vide en v0 -> etat d'accueil). */}
        <div className="flex flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-6 py-8 text-center">
          <span
            aria-hidden="true"
            className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent-tint text-accent-soft-ink"
          >
            <IconStar2 size={26} />
          </span>

          <div className="flex flex-col gap-1.5">
            <p className="text-base font-semibold text-ink-900">
              Posez une question à Taki
            </p>
            <p className="max-w-[18rem] text-sm leading-relaxed text-ink-500">
              Posez une question sur vos apps, vos contacts, vos activités, et Taki
              vous répond.
            </p>
          </div>

          {/* Amorces cliquables : pre-remplissent le champ (toujours coquille v0). */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setDraft(suggestion)}
                className={cn(
                  'rounded-pill border border-border bg-surface-1 px-3 py-1.5',
                  'text-xs font-medium text-ink-600 transition-colors',
                  'hover:border-border-strong hover:text-ink-900',
                )}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>

        {/* Barre de saisie. Coquille v0 : l'envoi ne contacte aucun backend. */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-2.5 border-t border-border-soft px-5 py-4"
        >
          <div className="flex items-center gap-2.5">
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              leftIcon={<IconStar2 size={16} />}
              placeholder="Posez votre question…"
              aria-label="Votre question pour Taki"
              className="flex-1"
            />
            <Button
              type="submit"
              variant="ai"
              size="lg"
              disabled={draft.trim().length === 0}
            >
              Envoyer
            </Button>
          </div>

          <div className="flex justify-center">
            <StatusBadge tone="neutral" label="v0 · aperçu, sans réponse réelle" />
          </div>
        </form>
      </aside>
    </div>
  );
}
